import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, Input, OnInit, ViewChild } from '@angular/core';

import { BetslipConfig, CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { CalculatedOdds, OddsFormatterService } from '@frontend/sports/odds/feature';
import { DecimalsPriceFormatterService } from '@frontend/sports/odds/feature/native-price-decimal-formatter';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { UserService } from '@frontend/sports/user/feature';
import { IntlService } from '@frontend/vanilla/core';
import { Store, createSelector } from '@ngrx/store';
import { EpcotConfigService, EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';
import { PayoutType } from 'packages/sports/web/app/src/tokens-base/token-base.models';
import { TokensCalculatorService } from 'packages/sports/web/app/src/tokens-base/tokens-calculator.service';
import { TokensSummaryService } from 'packages/sports/web/app/src/tokens-base/tokens-summary.service';
import { Observable, map } from 'rxjs';

import { CurrencyPipe } from '../../../../../../base/pipes/currency.pipe';
import { selectIsLinearBetslip } from '../../../../../../base/store/selectors';
import { IBetslipRootState } from '../../../../../../base/store/state';
import { BetslipType } from '../../../../../../core/betslip-type';
import { betslipPicksListSelector } from '../../../../../picks/selectors';
import { filterPicksForType } from '../../../../../picks/services/linear-betslip-pick.utils';
import { betslipCurrentTypeSelector } from '../../../../../types/base/selectors';
import { betslipTypeStateSelector } from '../../../../../types/selectors';
import { CriteriaType, OddsBoostToken, RewardTokenContext } from '../../../../reward-tokens.model';
import { selectCurrentEligibilityState, selectEligibilityStateFactory } from '../../../../selectors';
import { RewardTokensBannerService, TokenCategories } from '../../../../services/reward-tokens-banner.service';
import { calcTotalOddsForOddsBoost, getPayoutExpiryInDays } from '../../../../services/reward-tokens.utils';
import { tokenBoostAnimations } from '../../token-boost-animations';
import { RewardTokenCardBaseComponent } from '../reward-token-card-base.component';
import { RewardTokenLimitation } from '../reward-token-limitations.component';

export const selectSelectedPicksByType = (betslipType: BetslipType | undefined) =>
    createSelector(betslipPicksListSelector, betslipTypeStateSelector, (pickList, types) => {
        return betslipType ? filterPicksForType(types, betslipType, pickList, { isSelected: true }) : [];
    });

export const selectCurrentTypePicks = createSelector(
    betslipCurrentTypeSelector,
    betslipPicksListSelector,
    betslipTypeStateSelector,
    (currentType, picksList, types) => {
        if (currentType === BetslipType.System) {
            return [];
        }

        const picks = currentType ? filterPicksForType(types, currentType, picksList, { isSelected: true }) : [];

        return currentType === BetslipType.Single && picks.length > 1 ? [] : picks;
    },
);

export const selectPicksWithoutContext = createSelector(selectIsLinearBetslip, selectCurrentTypePicks, (isLinear, currentTypePicks) =>
    isLinear ? [] : currentTypePicks,
);

export const selectSelectedPicksForTokenContext = (context: RewardTokenContext) =>
    createSelector(selectSelectedPicksByType(context.betslipType), (picks) =>
        context.pickId ? picks.filter((pick) => pick.id.isEqual(context.pickId)) : picks,
    );

@Component({
    selector: 'bs-odds-boost-card',
    templateUrl: './odds-boost-card.component.html',
    styleUrls: ['../../cards/styles/reward-token-card-shared.scss', './odds-boost-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [tokenBoostAnimations],
    standalone: false,
})
export class OddsBoostCardComponent extends RewardTokenCardBaseComponent<OddsBoostToken> implements OnInit {
    @HostBinding('class') className = 'odds-boost-card';

    @HostBinding('class.reward-drawer__details') get formCoupon(): boolean {
        return this.isRewardDrawerEnabled;
    }

    @Input() tokenContext?: RewardTokenContext;

    odds$: Observable<CalculatedOdds | null>;
    boostedOdds: CalculatedOdds | null;
    oddsDisplayFormat: OddsDisplayFormat = OddsDisplayFormat.EU;
    module = EpcotModule.Rewards;
    isEpcotEnabled = this.epcotConfigService.isEnabled();
    maxDecimalPlaces: number;
    promoHubUrlTokenType: string;

    expireTokenReward: RewardTokenLimitation[];
    betType: RewardTokenLimitation[];
    tokenPayoutTitle: string;
    @ViewChild('tokensBannerContainer') container: ElementRef;

    @Input() override isRewardDrawerEnabled: boolean;
    scrollHeight = 0;
    tokenInfoUrl = '';
    tokenLimitations: RewardTokenLimitation[];
    tokenCategories: TokenCategories;
    isMinLegsValid$: Observable<boolean>;

    constructor(
        public override sitecore: Sitecore,
        private interpolationPipe: StringInterpolationPipe,
        protected override crmConfig: CrmConfig,
        protected override intlService: IntlService,
        private nativeDecimalPipe: DecimalsPriceFormatterService,
        private currencyPipe: CurrencyPipe,
        protected override tokensPayoutInfo: TokensSummaryService,
        protected userService: UserService,
        private tokensCalculator: TokensCalculatorService,
        private store: Store<IBetslipRootState>,
        private betslipConfig: BetslipConfig,
        private epcotConfigService: EpcotConfigService,
        protected override oddsFormatterService: OddsFormatterService,
        private rewardTokensBannerService: RewardTokensBannerService,
    ) {
        super(sitecore, crmConfig, userService, intlService, oddsFormatterService, tokensPayoutInfo);

        this.maxDecimalPlaces = this.crmConfig.tokens.boostedOddsMaxDecimalPlaces;
    }

    ngOnInit(): void {
        this.oddsDisplayFormat = this.userOddsFormatSettingsService.oddsFormat;

        const selectedPicks$ = this.tokenContext
            ? this.store.select(selectSelectedPicksForTokenContext(this.tokenContext))
            : this.store.select(selectPicksWithoutContext);

        this.odds$ = selectedPicks$.pipe(
            map((picks) => {
                const odds = picks.length ? calcTotalOddsForOddsBoost(picks, this.betslipConfig.roundPriceValues) : null;
                if (odds) {
                    this.boostedOdds = this.tokensCalculator.getBoostedNativeOdds2(
                        odds,
                        this.token.boostFactor,
                        this.token.oddsBoostFormula,
                        this.oddsDisplayFormat,
                    );
                }

                return odds;
            }),
        );

        this.expireTokenReward = super.getTokenUserExpire();
        this.betType = super.getTokenBetType();
        this.tokenPayoutTitle = this.isRewardDrawerEnabled
            ? `${this.sitecore.crmTokens.cards.TokenPayout}:`
            : this.sitecore.crmTokens.oddsBoost.card.BoostedWinningsPayout;
        this.tokenLimitations = this.getTokenLimitations();
        this.tokenCategories = this.rewardTokensBannerService.getTokenCategories(this.token);

        const eligibilityState$ = this.tokenContext
            ? this.store.select(selectEligibilityStateFactory(this.tokenContext))
            : this.store.select(selectCurrentEligibilityState);

        this.isMinLegsValid$ = eligibilityState$.pipe(
            map((elgibilities) => {
                const eligibilityState = elgibilities.find((eligibleState) => eligibleState.tokenId === this.token.userTokenId);

                return !!eligibilityState && eligibilityState.isEligible && eligibilityState.softCriteriasValidity[CriteriaType.MinimumLegs] === true;
            }),
        );
        this.promoHubUrlTokenType = this.sitecore.crmTokens.ctaLinks.promoCtaLinks.OddsBoost;
    }

    get tokenTitle(): string {
        if (this.token.tokenType === 'DEFAULT') {
            return this.sitecore.crmTokens.oddsBoost.card.OddsBoostTypeRegular;
        } else if (this.token.tokenType === 'MEGA') {
            return this.sitecore.crmTokens.oddsBoost.card.OddsBoostTypeMega;
        } else if (this.token.tokenType === 'SUPER') {
            return this.sitecore.crmTokens.oddsBoost.card.OddsBoostTypeSuper;
        } else {
            if (this.token.tokenType === 'CUSTOM' && this.token.customTitle) {
                return this.token.customTitle;
            }

            return this.sitecore.crmTokens.messages.OddsBoost;
        }
    }

    override getTokenLimitations(): RewardTokenLimitation[] {
        const result: RewardTokenLimitation[] = super.getTokenLimitations();
        if (this.token.maximumStake) {
            result.push({
                name: this.sitecore.crmTokens.oddsBoost.card.MaxStake,
                value: this.currencyPipe.transform(this.token.maximumStake, true, 2, true, undefined, undefined, false),
            });
        }
        if (this.token.maxWinningsBoost) {
            result.push({
                name: this.sitecore.crmTokens.oddsBoost.card.MaxRewardsWinnings,
                value: this.currencyPipe.transform(this.token.maxWinningsBoost, true, 2, true, undefined, undefined, false),
            });
        }

        if (this.token.minimumStake) {
            result.push({
                name: this.sitecore.crmTokens.oddsBoost.card.MinStake,
                value: this.currencyPipe.transform(this.token.minimumStake, true, 2, true, undefined, undefined, false),
            });
        }

        return result;
    }

    get payoutDetails(): string {
        switch (this.token.payoutType) {
            case PayoutType.FreeBet:
                return this.isRewardDrawerEnabled
                    ? this.interpolationPipe.transform(this.sitecore.crmTokens.oddsBoost.card.BoostedWinningsPayout, {
                          payoutType: this.sitecore.crmTokens.oddsBoost.card.FreebetPayoutTemplate,
                      })
                    : this.interpolationPipe.transform(this.sitecore.crmTokens.oddsBoost.card.FreebetPayoutTemplate, {
                          payoutExpiry: getPayoutExpiryInDays(this.token),
                      });
            case PayoutType.Bonus:
                return this.isRewardDrawerEnabled
                    ? this.interpolationPipe.transform(this.sitecore.crmTokens.oddsBoost.card.BoostedWinningsPayout, {
                          payoutType: this.sitecore.crmTokens.oddsBoost.card.BonusPayoutTemplate,
                      })
                    : this.interpolationPipe.transform(this.sitecore.crmTokens.oddsBoost.card.BonusPayoutTemplate, {
                          wageringMultiplier: this.token.wageringMultiplier ? this.token.wageringMultiplier.toString() : '',
                          minOdds: this.token.minOdds ? this.nativeDecimalPipe.format(this.token.minOdds) : '',
                          payoutExpiry: getPayoutExpiryInDays(this.token),
                      });
            case PayoutType.Cash:
                return this.isRewardDrawerEnabled
                    ? this.interpolationPipe.transform(this.sitecore.crmTokens.oddsBoost.card.BoostedWinningsPayout, {
                          payoutType: this.sitecore.crmTokens.oddsBoost.card.CashPayoutTemplate,
                      })
                    : this.sitecore.crmTokens.oddsBoost.card.CashPayoutTemplate;
        }

        return '';
    }

    get oddsBoostPercentageDetails(): string {
        return this.interpolationPipe.transform(this.sitecore.crmTokens.oddsBoost.card.OddsBoostPercentageTemplate, {
            boostFactor: this.token.boostFactor.toString(),
        });
    }
}

export function oddsChanged(previousOdd: string | null, newOdd: string | null): boolean {
    return !!newOdd && previousOdd !== newOdd;
}
